import { useMemo } from "react"

import type { PlanInterval } from "@productlane/lib/src/browser"

import { api } from "./api"
import { toPlanInterval } from "./plan-gate"

export interface PaymentIssue {
	status: "past_due" | "unpaid" | "incomplete"
	amountDue: number | null
	currency: string | null
	invoiceUrl: string | null
	nextPaymentAttempt: string | null
}

export function useTrial(): {
	hasTrialEnded: boolean
	isScalePlan: boolean
	daysLeft: number
	hasPaymentMethod: boolean
	isTrial: boolean
	isStartupProgram: boolean
	hadRecentCancellation: boolean
	cancelAtPeriodEnd: boolean
	currentPeriodEnd: string | null
	effectivePlan: string | undefined
	billingInterval: PlanInterval
	paymentIssue: PaymentIssue | null
} {
	const { data: activePlan } = api.workspace.activePlan.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})

	const { data: trialInfo } = api.workspace.trialInfo.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})

	const hasTrialEnded = useMemo(() => {
		if (trialInfo?.isTrial) return false
		const effectivePlan = trialInfo?.plan ?? activePlan
		if (effectivePlan === "free") {
			return true
		}
		return false
	}, [activePlan, trialInfo])

	const isScalePlan = useMemo(() => {
		const effectivePlan = trialInfo?.plan ?? activePlan
		if (
			effectivePlan === "business" ||
			effectivePlan === "enterprise" ||
			effectivePlan === "scale"
		) {
			return true
		}
		return false
	}, [activePlan, trialInfo])

	const daysLeft = useMemo(() => {
		return trialInfo?.daysLeft ?? 0
	}, [trialInfo])

	const hasPaymentMethod = useMemo(() => {
		return trialInfo?.hasPaymentMethod ?? false
	}, [trialInfo])

	const isTrial = useMemo(() => {
		return trialInfo?.isTrial ?? false
	}, [trialInfo])

	const isStartupProgram = useMemo(() => {
		return trialInfo?.isStartupProgram ?? false
	}, [trialInfo])

	const hadRecentCancellation = useMemo(() => {
		return trialInfo?.hadRecentCancellation ?? false
	}, [trialInfo])

	const cancelAtPeriodEnd = useMemo(() => {
		return trialInfo?.cancelAtPeriodEnd ?? false
	}, [trialInfo])

	const currentPeriodEnd = useMemo(() => {
		return trialInfo?.currentPeriodEnd ?? null
	}, [trialInfo])

	const effectivePlan = useMemo(() => {
		return trialInfo?.plan ?? activePlan
	}, [activePlan, trialInfo])

	const billingInterval = useMemo(() => {
		return toPlanInterval(trialInfo?.interval)
	}, [trialInfo])

	const paymentIssue = useMemo(() => {
		return trialInfo?.paymentIssue ?? null
	}, [trialInfo])

	return {
		hasTrialEnded,
		isScalePlan,
		daysLeft,
		hasPaymentMethod,
		isTrial,
		isStartupProgram,
		hadRecentCancellation,
		cancelAtPeriodEnd,
		currentPeriodEnd,
		effectivePlan,
		billingInterval,
		paymentIssue,
	}
}
